// The Prime composer: an auto-growing box that takes text, code and pictures.
// Self-contained: it owns the draft, the attachments and the keys, and hands
// the finished message to onSend. Enter sends on a phone and is a newline on a
// desktop (Cmd/Ctrl+Enter sends there) unless the host passes `enterSends`;
// inside a ``` fence the box is monospace and Tab indents. Laid out like Flux's Composer: one
// rounded container (it draws the focus, the textarea draws nothing) with a header for the
// attachments, the textarea, and a footer of small leading actions (attach, commands) and
// trailing ones (mic, the host's `beside`, the filled send). The textarea grows from two lines
// to eight, then scrolls. Every thread gets the same box: a host passes its `commands` (the
// slash picker lives here, above the box) and either `onMic` (a voice conversation) or
// `dictate` (the device's recogniser writes into the draft).
import { useCallback, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Mic, Paperclip, SendHorizontal, Slash, X } from 'lucide-react'
import { matchCommands, type ChatCommand } from '@/lib/chatCommands'
import { useDictation, voiceAvailable } from '@/voice/listen'
import { tick } from '@/lib/haptics'
import { useKeyboardUp } from '@/hooks/useVisualViewport'
import { cx } from '@/components/ui'
import {
  IMAGE_TYPES,
  MAX_IMAGES,
  MAX_IMAGE_DATA,
  canSend,
  enterEdit,
  boxHeight,
  fitWithin,
  inFence,
  keyAction,
  maxLinesFor,
  minLinesFor,
  refuseFile,
  tabEdit,
  type Attached,
  type Edit,
} from '@/lib/composer'

export interface ComposerMessage {
  text: string
  images: { media_type: string; data: string }[]
}

const finePointer = () => typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches

// Flux's footer buttons: small, square-ish and quiet; only send is filled
const LEAD =
  'h-9 w-9 rounded-[10px] text-muted hover:bg-raised hover:text-fg flex items-center justify-center transition-colors disabled:opacity-40 disabled:hover:bg-transparent'

let seq = 0

/** Read a picture, scale it and encode it as JPEG within the server's limit. */
const prepare = async (file: File): Promise<Attached> => {
  const bmp = await createImageBitmap(file)
  let scale = 1
  for (let attempt = 0; attempt < 4; attempt++) {
    const side = fitWithin(Math.round(bmp.width * scale), Math.round(bmp.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = side.w
    canvas.height = side.h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Cannot read that picture')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, side.w, side.h)
    ctx.drawImage(bmp, 0, 0, side.w, side.h)
    const url = canvas.toDataURL('image/jpeg', attempt ? 0.7 : 0.88)
    const data = url.slice(url.indexOf(',') + 1)
    if (data.length <= MAX_IMAGE_DATA) {
      bmp.close()
      return { id: `img-${++seq}`, media_type: 'image/jpeg', data, name: file.name || 'Picture' }
    }
    scale *= 0.7
  }
  bmp.close()
  throw new Error('That picture is too large')
}

export const PrimeComposer = ({
  onSend,
  busy = false,
  autoFocus = false,
  placeholder = 'Ask, plan, or tell it something to file…',
  onError,
  className,
  compact = false,
  label,
  inputTestId = 'ask-prime-input',
  sendTestId = 'ask-prime-send',
  enterSends = false,
  pictures = true,
  beside,
  sendLabel = 'Send to Prime',
  value,
  onValueChange,
  onKeyFirst,
  disabled = false,
  onMic,
  commands,
  onCommand,
  dictate = false,
  initial = '',
}: {
  onSend: (m: ComposerMessage) => Promise<void> | void
  busy?: boolean
  autoFocus?: boolean
  placeholder?: string
  onError?: (message: string) => void
  className?: string
  /** a narrow host (the side panel): a lower floor for the box */
  compact?: boolean
  label?: string
  inputTestId?: string
  sendTestId?: string
  /** Enter sends on a desktop too (the side panel keeps the key it had) */
  enterSends?: boolean
  /** pictures can be attached */
  pictures?: boolean
  /** something beside Send, such as a working session's Stop */
  beside?: ReactNode
  sendLabel?: string
  /** a host that reads or sets the draft (the chat's slash picker) passes both; else the box keeps it */
  value?: string
  onValueChange?: (text: string) => void
  /** a host's own keys first: return true when it took the key, and the box does nothing with it */
  onKeyFirst?: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean
  disabled?: boolean
  /** the host has voice: shows a mic beside Send that starts it */
  onMic?: () => void
  /** slash commands for this thread: a / button, and a picker above the box as one is typed */
  commands?: ChatCommand[]
  /** a command picked (the draft is cleared first) */
  onCommand?: (c: ChatCommand) => void
  /** no voice of its own: the mic dictates into the draft (the device's recogniser) */
  dictate?: boolean
  /** a draft handed over from elsewhere (an Ask Prime made while Prime was not running), in the box unsent */
  initial?: string
}) => {
  const [own, setOwn] = useState(initial)
  const text = value ?? own
  const setText = (t: string) => {
    setOwn(t)
    onValueChange?.(t)
  }
  const [images, setImages] = useState<Attached[]>([])
  // the slash picker: the commands matching the draft, and the one the arrows are on
  const matched = commands ? matchCommands(text, commands) : []
  const [pick, setPick] = useState(0)
  const chosen = matched[Math.min(pick, matched.length - 1)]
  const runCommand = (c: ChatCommand) => {
    setText('')
    setPick(0)
    onCommand?.(c)
  }
  // dictation: what is heard is added to the draft when the speaker stops
  const [canDictate] = useState(() => dictate && voiceAvailable())
  const dictation = useDictation((heard) => {
    const words = heard.trim()
    if (!words) return
    setText(text ? `${text.replace(/\s+$/, '')} ${words}` : words)
  })
  const [fenced, setFenced] = useState(false)
  const [over, setOver] = useState(false)
  const [fine] = useState(finePointer)
  const area = useRef<HTMLTextAreaElement>(null)
  const files = useRef<HTMLInputElement>(null)
  const pending = useRef(0)
  // pictures still being read and encoded: sending waits for them, or the words would go without
  // the picture and the picture would land in the emptied box after
  const [preparing, setPreparing] = useState(0)
  const haveImages = useRef(0)
  useLayoutEffect(() => {
    haveImages.current = images.length
  }, [images.length])

  const keyboardUp = useKeyboardUp()
  // grow with the text: from a floor to a cap, then scroll
  useLayoutEffect(() => {
    const el = area.current
    if (!el) return
    const cs = getComputedStyle(el)
    const size = parseFloat(cs.fontSize) || 15
    const line = parseFloat(cs.lineHeight) || size * 1.625
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
    el.style.height = 'auto'
    const box = boxHeight(
      el.scrollHeight,
      line,
      pad,
      minLinesFor(compact, fine),
      maxLinesFor(keyboardUp, fine),
    )
    el.style.height = `${box.height}px`
    el.style.overflowY = box.scrolls ? 'auto' : 'hidden'
  }, [text, compact, fine, keyboardUp, fenced])

  const add = useCallback(
    async (list: File[]) => {
      for (const f of list) {
        const why = refuseFile(f, haveImages.current + pending.current)
        if (why) {
          onError?.(why)
          continue
        }
        pending.current++
        setPreparing((n) => n + 1)
        try {
          const img = await prepare(f)
          setImages((cur) => (cur.length >= MAX_IMAGES ? cur : [...cur, img]))
        } catch (e) {
          onError?.((e as Error).message || 'Cannot read that picture')
        } finally {
          pending.current--
          setPreparing((n) => n - 1)
        }
      }
    },
    [onError],
  )

  const send = async () => {
    if (busy || disabled || preparing > 0 || !canSend(text, images.length)) return
    try {
      await onSend({
        text: text.trim(),
        images: images.map(({ media_type, data }) => ({ media_type, data })),
      })
      setText('')
      setImages([])
      setFenced(false)
    } catch {
      // the caller says why; the draft stays
    }
  }

  const apply = (e: Edit) => {
    setText(e.text)
    setFenced(inFence(e.text, e.start))
    requestAnimationFrame(() => area.current?.setSelectionRange(e.start, e.end))
  }

  const ready = canSend(text, images.length) && !busy && !disabled && preparing === 0

  return (
    <div className="relative">
      {matched.length > 0 && (
        <ul
          className="absolute inset-x-0 bottom-full mb-1.5 card p-1.5 flex flex-col gap-0.5 max-h-64 overflow-y-auto z-10"
          data-testid="chat-commands"
        >
          {matched.map((c) => (
            <li key={c.name}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  runCommand(c)
                }}
                className={cx(
                  'w-full text-left px-2.5 py-2 min-h-10 flex items-baseline gap-2',
                  c === chosen ? 'bg-raised' : 'hover:bg-raised',
                )}
                style={{ borderRadius: 'var(--radius-card-inner)' }}
                data-testid={`chat-command-${c.name}`}
              >
                <span className="text-sm font-medium">/{c.name}</span>
                <span className="text-xs text-faint truncate">{c.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div
        className={cx(
          'flex flex-col rounded-[var(--radius-composer)] border bg-panel shadow-[0_10px_30px_-18px_rgb(0_0_0/0.45)]',
          // the box draws the focus, the textarea inside draws nothing (no box in a box)
          over ? 'border-accent' : 'border-line focus-within:border-accent',
          className,
        )}
        onDragOver={(e) => {
          if (pictures && e.dataTransfer.types.includes('Files')) {
            e.preventDefault()
            setOver(true)
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false)
          const dropped = pictures ? [...e.dataTransfer.files] : []
          if (dropped.length) {
            e.preventDefault()
            void add(dropped)
          }
        }}
        data-testid="prime-composer"
      >
        {(images.length > 0 || preparing > 0) && (
          <ul
            className="flex gap-2 overflow-x-auto px-3 pt-3"
            aria-label="Attached pictures"
            data-testid="prime-composer-chips"
          >
            {images.map((im) => (
              <li key={im.id} className="relative shrink-0">
                <img
                  src={`data:${im.media_type};base64,${im.data}`}
                  alt={im.name}
                  className="h-16 w-16 rounded-[var(--radius-card-inner)] border border-line object-cover"
                />
                <button
                  type="button"
                  onClick={() => setImages((cur) => cur.filter((x) => x.id !== im.id))}
                  aria-label={`Remove ${im.name}`}
                  className="absolute -right-1.5 -top-1.5 h-6 w-6 rounded-full bg-raised border border-line flex items-center justify-center text-fg-2"
                >
                  <X size={13} aria-hidden />
                </button>
              </li>
            ))}
            {Array.from({ length: preparing }, (_, i) => (
              <li
                key={`preparing-${i}`}
                className="h-16 w-16 shrink-0 rounded-[var(--radius-card-inner)] border border-line bg-raised"
                aria-label="Adding a picture"
                data-testid="prime-composer-preparing"
              />
            ))}
          </ul>
        )}
        {dictation.problem && !dictation.listening && (
          <p className="px-3 pt-2.5 text-[13px] text-muted" data-testid="prime-composer-heard-problem">
            Dictation stopped: {dictation.problem}
          </p>
        )}
        {dictation.listening && (
          <p
            className="px-3 pt-2.5 text-[13px] text-accent-text"
            aria-live="polite"
            data-testid="prime-composer-heard"
          >
            {dictation.heard || 'Listening…'}
          </p>
        )}
        <textarea
          ref={area}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setPick(0)
            setFenced(inFence(e.target.value, e.target.selectionStart))
          }}
          onSelect={(e) => setFenced(inFence(e.currentTarget.value, e.currentTarget.selectionStart))}
          onKeyDown={(e) => {
            if (onKeyFirst?.(e)) return
            if (matched.length > 0) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault()
                setPick((p) => (p + (e.key === 'ArrowDown' ? 1 : matched.length - 1)) % matched.length)
                return
              }
              if (chosen && (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey))) {
                e.preventDefault()
                runCommand(chosen)
                return
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                setText('')
                return
              }
            }
            const el = e.currentTarget
            if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey) {
              const edit = tabEdit(el.value, el.selectionStart, el.selectionEnd, e.shiftKey)
              if (edit) {
                e.preventDefault()
                apply(edit)
              }
              return
            }
            const act = keyAction(e.nativeEvent, fine, enterSends)
            if (act === 'send') {
              e.preventDefault()
              void send()
            } else if (act === 'newline' && !e.shiftKey && !e.altKey) {
              const edit = enterEdit(el.value, el.selectionStart, el.selectionEnd)
              if (edit) {
                e.preventDefault()
                apply(edit)
              }
            }
          }}
          onPaste={(e) => {
            const pics = pictures
              ? [...e.clipboardData.files].filter((f) => IMAGE_TYPES.includes(f.type))
              : []
            if (pics.length) {
              e.preventDefault()
              void add(pics)
            }
          }}
          rows={2}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={label}
          spellCheck={!fenced}
          className={cx(
            // the global textarea rule is unlayered (and .bare-input is for inputs), so this needs !
            // the height is set from the lines in the effect above
            'bare-input w-full resize-none !border-0 !bg-transparent !shadow-none !outline-none !ring-0 px-3 pt-3 pb-1 leading-relaxed',
            fenced ? 'font-mono text-[13px]' : 'text-[15px]',
          )}
          data-testid={inputTestId}
        />
        <div className="flex items-center justify-between gap-2 p-2" data-testid="prime-composer-footer">
          <div className="flex items-center gap-1">
            {pictures && (
              <button
                type="button"
                onClick={() => files.current?.click()}
                aria-label="Attach a picture"
                disabled={images.length >= MAX_IMAGES}
                className={LEAD}
                data-testid="prime-composer-attach"
              >
                <Paperclip size={17} aria-hidden />
              </button>
            )}
            <input
              ref={files}
              type="file"
              accept={IMAGE_TYPES.join(',')}
              multiple
              hidden
              data-testid="prime-composer-file"
              onChange={(e) => {
                void add([...(e.target.files ?? [])])
                e.target.value = ''
              }}
            />
            {commands && commands.length > 0 && (
              <button
                type="button"
                // keep the box focused so the picker takes the keys
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setText('/')
                  setPick(0)
                  area.current?.focus()
                }}
                aria-label="Commands"
                // commands start a draft; one already begun is never overwritten
                disabled={disabled || (text !== '' && !text.startsWith('/'))}
                className={LEAD}
                data-testid="prime-composer-slash"
              >
                <Slash size={16} aria-hidden />
              </button>
            )}
            {fine && !enterSends && (
              <span className="hidden md:inline px-1 text-xs text-faint">⌘/Ctrl+Enter to send</span>
            )}
          </div>
          <span className="flex items-center gap-1">
            {onMic ? (
              <button
                type="button"
                onClick={onMic}
                aria-label="Talk"
                disabled={disabled}
                className={LEAD}
                data-testid="prime-composer-mic"
              >
                <Mic size={17} aria-hidden />
              </button>
            ) : (
              canDictate && (
                <button
                  type="button"
                  onClick={() => {
                    tick(6)
                    dictation.start()
                  }}
                  aria-label={dictation.listening ? 'Stop dictating' : 'Dictate'}
                  aria-pressed={dictation.listening}
                  disabled={disabled}
                  className={cx(LEAD, dictation.listening && '!bg-accent-soft !text-accent-text')}
                  data-testid="prime-composer-mic"
                >
                  <Mic size={17} aria-hidden />
                </button>
              )
            )}
            {beside}
            <button
              type="button"
              onClick={() => void send()}
              disabled={!ready}
              aria-label={sendLabel}
              className="h-9 w-9 shrink-0 rounded-[10px] bg-accent text-on-accent flex items-center justify-center transition-opacity disabled:opacity-35"
              data-testid={sendTestId}
            >
              <SendHorizontal size={17} aria-hidden />
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
